/** Time helpers. Replaces the four duplicated relative-time implementations
 *  that were spread across the old templates.
 *
 *  Note: unlike the design prototype there is no module-level `now` — that
 *  froze the clock at page load, so nothing ever ticked. Every call reads
 *  Date.now() afresh. */

/** The API mixes unix seconds (next_run, last_success, sent_at) with ISO-8601
 *  strings (last_run). Normalise both to epoch milliseconds. */
export function toMs(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    // Unix seconds are ~1.7e9 today; milliseconds ~1.7e12.
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** "6m ago" / "in 2h" / "just now". Returns a dash for missing values. */
export function relTime(value: string | number | null | undefined, fallback = '—'): string {
  const ms = toMs(value);
  if (ms === null) return fallback;

  const delta = ms - Date.now();
  const abs = Math.abs(delta);
  const future = delta > 0;

  if (abs < 45_000) return future ? 'in a moment' : 'just now';

  let n: number;
  let unit: string;
  if (abs < HOUR) {
    n = Math.round(abs / MIN);
    unit = 'm';
  } else if (abs < DAY) {
    n = Math.round(abs / HOUR);
    unit = 'h';
  } else {
    n = Math.round(abs / DAY);
    unit = 'd';
  }
  return future ? `in ${n}${unit}` : `${n}${unit} ago`;
}

/** Local wall-clock time, HH:MM. */
export function clock(value: string | number | null | undefined, fallback = '—'): string {
  const ms = toMs(value);
  if (ms === null) return fallback;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function dateTime(value: string | number | null | undefined, fallback = '—'): string {
  const ms = toMs(value);
  if (ms === null) return fallback;
  return new Date(ms).toLocaleString();
}

/** 45 -> "45s", 300 -> "5m", 3600 -> "1h", 5400 -> "1h30m".
 *  The design used display strings like "45s"; the API stores integer seconds. */
export function formatInterval(seconds: number | null | undefined, fallback = '—'): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return fallback;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem ? `${m}m${rem}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h}h${m}m` : `${h}h`;
}

/** Execution times, which are sub-second often enough to need ms. */
export function formatDuration(seconds: number | null | undefined, fallback = '—'): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return fallback;
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return formatInterval(seconds);
}
